import { Component, input, output, ChangeDetectionStrategy, ViewEncapsulation, computed } from "@angular/core";
import { CommonModule } from "@angular/common";
import type { Message } from "@ag-ui/core";
import { AgentChatAssistantMessage } from "./ai-chat-assistant-message";
import { AgentChatUserMessage } from "./ai-chat-user-message";
import { AgentChatMessageViewCursor } from "./ai-chat-message-view-cursor";
import { cn } from "../utils/utils";

@Component({
  selector: "agent-chat-message-view",
  standalone: true,
  host: { "data-agent-ui": "" },
  imports: [CommonModule, AgentChatAssistantMessage, AgentChatUserMessage, AgentChatMessageViewCursor],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <div [class]="computedClass()">
      @for ( message of messagesValue(); track trackByMessageId($index, message) ) { @if (message && message.role ===
      "assistant") {
      <agent-chat-assistant-message
        [message]="message"
        [messages]="messagesValue()"
        [isLoading]="isLoadingValue()"
        [inputClass]="assistantMessageClass()"
        (thumbsUp)="handleAssistantThumbsUp($event)"
        (thumbsDown)="handleAssistantThumbsDown($event)"
        (readAloud)="handleAssistantReadAloud($event)"
        (regenerate)="handleAssistantRegenerate($event)"
      >
      </agent-chat-assistant-message>
      } @else if (message && message.role === "user") {
      <agent-chat-user-message [message]="message" [inputClass]="userMessageClass()"> </agent-chat-user-message>
      } } @if (showCursorValue()) {
      <agent-chat-message-view-cursor [inputClass]="cursorClass()"> </agent-chat-message-view-cursor>
      }
    </div>
  `,
})
export class AgentChatMessageView {
  messages = input<Message[]>([]);

  showCursor = input<boolean>(false);

  isLoading = input<boolean>(false);

  inputClass = input<string | undefined>();

  assistantMessageClass = input<string | undefined>();

  userMessageClass = input<string | undefined>();

  cursorClass = input<string | undefined>();

  assistantMessageThumbsUp = output<{ message: Message }>();

  assistantMessageThumbsDown = output<{ message: Message }>();

  assistantMessageReadAloud = output<{ message: Message }>();

  assistantMessageRegenerate = output<{ message: Message }>();

  userMessageCopy = output<{ message: Message }>();

  userMessageEdit = output<{ message: Message }>();

  protected messagesValue = computed(() => this.messages());

  protected showCursorValue = computed(() => this.showCursor());

  protected isLoadingValue = computed(() => this.isLoading());

  computedClass = computed(() => cn("achat-message-view", this.inputClass()));

  trackByMessageId(index: number, message: Message): string {
    return message?.id || `index-${index}`;
  }

  constructor() {}

  handleAssistantThumbsUp(event: { message: Message }): void {
    this.assistantMessageThumbsUp.emit(event);
  }

  handleAssistantThumbsDown(event: { message: Message }): void {
    this.assistantMessageThumbsDown.emit(event);
  }

  handleAssistantReadAloud(event: { message: Message }): void {
    this.assistantMessageReadAloud.emit(event);
  }

  handleAssistantRegenerate(event: { message: Message }): void {
    this.assistantMessageRegenerate.emit(event);
  }
}
